import RefreshIcon from '@mui/icons-material/Refresh';
import { Alert, AlertTitle, Box, Button, Stack, Typography } from '@mui/material';
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { withTranslation, type WithTranslation } from 'react-i18next';

interface Props extends WithTranslation {
  children: ReactNode;
  /** Changing this resets the boundary, e.g. on navigation. */
  resetKey?: string;
}

interface State {
  error: Error | null;
  info: string | null;
}

/**
 * Catches a render crash and shows something readable instead of a blank page.
 *
 * Without this, any thrown error during render unmounts the whole tree and
 * leaves a white screen with nothing to read and nothing to click — which is
 * exactly what a user reports as "it stopped working", with no way to tell what
 * broke. The message is deliberately plain and offers a reload; the details are
 * collapsed behind a disclosure for whoever is debugging.
 *
 * It must be a class component: React has no hook equivalent of
 * `componentDidCatch`.
 */
class ErrorBoundaryBase extends Component<Props, State> {
  override state: State = { error: null, info: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Still logged, so the browser console keeps the full stack.
    console.error('Render error caught by boundary:', error, info.componentStack);
    this.setState({ info: info.componentStack ?? null });
  }

  override componentDidUpdate(previous: Props): void {
    // Navigating away from a broken screen should not stay broken.
    if (this.state.error && previous.resetKey !== this.props.resetKey) {
      this.setState({ error: null, info: null });
    }
  }

  override render(): ReactNode {
    const { error, info } = this.state;
    const { t, children } = this.props;

    if (!error) {
      return children;
    }

    return (
      <Box sx={{ p: 3, display: 'grid', placeItems: 'center', minHeight: '60vh' }}>
        <Stack spacing={2} sx={{ maxWidth: 640, width: '100%' }}>
          <Alert severity="error">
            <AlertTitle>{t('errors.boundaryTitle')}</AlertTitle>
            {t('errors.boundaryBody')}
          </Alert>

          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={() => window.location.reload()}
            >
              {t('errors.reload')}
            </Button>
            <Button onClick={() => this.setState({ error: null, info: null })}>
              {t('common.retry')}
            </Button>
          </Stack>

          <Box component="details" sx={{ color: 'text.secondary' }}>
            <Box component="summary" sx={{ cursor: 'pointer' }}>
              <Typography variant="body2" component="span">
                {t('errors.boundaryDetails')}
              </Typography>
            </Box>
            <Typography
              variant="caption"
              component="pre"
              sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', mt: 1 }}
            >
              {error.message}
              {info ? `\n${info}` : ''}
            </Typography>
          </Box>
        </Stack>
      </Box>
    );
  }
}

export const ErrorBoundary = withTranslation()(ErrorBoundaryBase);
